import styled from 'styled-components'
import { ComponentLayout } from '../../../components/layouts/ComponentLayout'
import { BodySmall, BodySmallMedium, Title2 } from '../../../components/styles/Typography'
import { theme } from '../../../theme/theme'
import { useTranslation } from 'react-i18next'
import { Arrow, Send, Info, ChatPlus } from '../../../components/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { OnClickButton } from '../../../components/buttons/onclick/OnClickButton'
import { useState, useEffect, useRef } from 'react'
import { useWindowWidth } from '../../../hooks/useWindowWidth'
import { isTabletOrMobile } from '../../../utils/devices'
import { useQueryClient } from '@tanstack/react-query'
import {
  useGetCaseQuery,
  useAddMessageMutation,
  useMarkMessageAsReadMutation,
} from '../../../api/ester-b2b-api/private/portalCase/portalCaseQueries'
import { QueryKeys } from '../../../api/ester-b2b-api/types'
import { MessageSenderType, CaseStatus } from '@estercare/ester-shared'
import { LoadingSpinner } from '../../../components/LoadingSpinner'

export const Conversation = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { caseId } = useParams<{ caseId: string }>()
  const queryClient = useQueryClient()
  const [newMessage, setNewMessage] = useState('')

  const width = useWindowWidth()
  const isMobileDevice = isTabletOrMobile(width)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const prevMessagesLengthRef = useRef(0)
  const { data: caseData, isLoading, isError } = useGetCaseQuery(caseId)
  const { mutate: markAsRead } = useMarkMessageAsReadMutation()
  const addMessageMutation = useAddMessageMutation({
    onSuccess: (updatedCase) => {
      setNewMessage('')
      if (caseId) {
        queryClient.setQueryData([QueryKeys.PortalCase, caseId], updatedCase)
      }
    },
  })

  useEffect(() => {
    if (!caseData) return

    const latestUnread = [...caseData.messages]
      .filter((msg) => msg.senderType === MessageSenderType.ESTERCARE && !msg.readAt)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
    if (!latestUnread) return

    markAsRead(
      { messageId: latestUnread.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [QueryKeys.Cases] })
          queryClient.invalidateQueries({ queryKey: [QueryKeys.PortalCase, caseId] })
        },
      }
    )
  }, [caseData, caseId, markAsRead, queryClient])

  useEffect(() => {
    if (!caseId) {
      navigate('/portal/messages', { replace: true })
    }
  }, [caseId, navigate])

  useEffect(() => {
    if (isError) {
      navigate('/portal/messages', { replace: true })
    }
  }, [isError, navigate])

  useEffect(() => {
    const currentLength = caseData?.messages.length ?? 0
    if (currentLength > prevMessagesLengthRef.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
    prevMessagesLengthRef.current = currentLength
  }, [caseData?.messages])

  const handleBack = () => {
    navigate('/portal/messages')
  }

  const handleStartNewConversation = () => {
    navigate('/portal/messages/new')
  }

  const handleSend = async () => {
    if (!newMessage.trim() || !caseId || addMessageMutation.isPending) return
    await addMessageMutation.mutateAsync({ caseId, message: newMessage })
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
  }

  if (!caseId || isError) {
    return null
  }

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    )
  }

  if (!caseData) {
    return null
  }

  return (
    <PageContainer>
      {isMobileDevice ? (
        <MobileHeader>
          <BackButton onClick={handleBack}>
            <Arrow size={24} color={theme.color.black} left />
          </BackButton>
        </MobileHeader>
      ) : (
        <ComponentLayout
          paddingTop={theme.spacing.xxlarge}
          paddingLeft={theme.spacing.large}
          paddingRight={theme.spacing.large}
          paddingBottom={theme.spacing.medium}
        >
          <Title2>{t('employee.messages.title')}</Title2>
        </ComponentLayout>
      )}

      {!isMobileDevice && (
        <SubHeader>
          <BackButton onClick={handleBack}>
            <Arrow size={24} color={theme.color.black} left />
          </BackButton>
        </SubHeader>
      )}

      <ChatContainer>
        <MessagesArea>
          {caseData.messages.map((msg) => (
            <MessageRow key={msg.id} $isUser={msg.senderType === MessageSenderType.USER}>
              <MessageBubble $isUser={msg.senderType === MessageSenderType.USER}>
                <MessageText>{msg.message}</MessageText>
                <MessageTime>{formatTime(msg.createdAt)}</MessageTime>
              </MessageBubble>
            </MessageRow>
          ))}

          {caseData.messages.length > 0 &&
            caseData.messages[caseData.messages.length - 1]?.senderType === MessageSenderType.USER && (
              <InfoBannerRow>
                <InfoBanner>
                  <Info size={24} color={theme.color.black} />
                  <InfoText>{t('employee.messages.responseInfo')}</InfoText>
                </InfoBanner>
              </InfoBannerRow>
            )}
          <div ref={messagesEndRef} />
        </MessagesArea>

        {caseData.status === CaseStatus.CLOSED ? (
          <ClosedContainer>
            <BodySmallMedium>{t('employee.messages.conversationClosedTitle')}</BodySmallMedium>
            <ClosedMessage>{t('employee.messages.conversationClosedDescription')}</ClosedMessage>
            <OnClickButton
              text={t('employee.messages.startConversation')}
              onClick={handleStartNewConversation}
              icon={<ChatPlus size={16} color={theme.color.white} />}
              buttonStyle={{ flexDirection: 'row-reverse', gap: `${theme.spacing.xsmall}px` }}
            />
          </ClosedContainer>
        ) : (
          <InputArea>
            <MessageInput
              placeholder={t('employee.messages.writeMessage')}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
            <SendButton onClick={handleSend} $disabled={!newMessage.trim() || addMessageMutation.isPending}>
              <Send size={20} color={theme.color.white} />
            </SendButton>
          </InputArea>
        )}
      </ChatContainer>
    </PageContainer>
  )
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  padding: ${theme.spacing.small}px 20px;
`

const SubHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.small}px;
  padding: ${theme.spacing.small}px ${theme.spacing.medium}px;
  border-bottom: 1px solid rgba(34, 34, 34, 0.1);
  background-color: ${theme.color.beigeLight};

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    padding: ${theme.spacing.medium}px;
    padding-bottom: 17px;
  }
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${theme.color.white};
  border: none;
  cursor: pointer;
  box-shadow:
    0px 1px 2px rgba(0, 0, 0, 0.06),
    0px 3px 9px rgba(0, 0, 0, 0.05);

  &:hover {
    background-color: ${theme.color.grey1};
  }
`

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: ${theme.color.beigeLight};
  overflow: hidden;
`

const MessagesArea = styled.div`
  flex: 1;
  padding: ${theme.spacing.mediumLarge}px ${theme.spacing.medium}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.mediumLarge}px;
  width: 100%;
  overflow-y: auto;

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    padding: ${theme.spacing.mediumLarge}px ${theme.spacing.xxlarge}px;
  }
`

const MessageRow = styled.div<{ $isUser: boolean }>`
  display: flex;
  width: 100%;
  justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
`

const MessageBubble = styled.div<{ $isUser: boolean }>`
  max-width: 70%;
  min-width: 20vw;
  padding: ${theme.spacing.small}px ${theme.spacing.medium}px;
  background-color: ${({ $isUser }) => ($isUser ? theme.color.white : theme.color.plum)};
  color: ${({ $isUser }) => ($isUser ? theme.color.black : theme.color.white)};
  border-radius: ${({ $isUser }) => ($isUser ? '16px 16px 0 16px' : '16px 16px 16px 0')};
  margin-left: ${({ $isUser }) => ($isUser ? 'auto' : '0')};
  margin-right: ${({ $isUser }) => ($isUser ? '0' : 'auto')};
`

const MessageText = styled(BodySmall)`
  white-space: pre-wrap;
  word-break: break-word;
`

const MessageTime = styled(BodySmallMedium)`
  font-size: 12px;
  opacity: 0.7;
  margin-top: ${theme.spacing.xsmall}px;
`

const InfoBannerRow = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: ${theme.spacing.small}px;
`

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.small}px;
  padding: ${theme.spacing.small}px ${theme.spacing.medium}px;
  background-color: ${theme.color.beigeDark};
  border-radius: ${theme.spacing.medium}px ${theme.spacing.medium}px ${theme.spacing.medium}px 0;
  max-width: 450px;
`

const InfoText = styled.p`
  font-family: ${theme.font.body};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${theme.color.black};
`

const ClosedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spacing.large}px ${theme.spacing.medium}px;
  background-color: ${theme.color.beigeLight};
`

const ClosedMessage = styled(BodySmall)`
  text-align: center;
  color: ${theme.color.black};
  max-width: 500px;
  margin-bottom: ${theme.spacing.large}px;
`

const InputArea = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${theme.spacing.small}px;
  padding: 0 ${theme.spacing.medium}px ${theme.spacing.medium}px;
  background-color: ${theme.color.beigeLight};

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    gap: ${theme.spacing.medium}px;
  }
`

const MessageInput = styled.textarea`
  flex: 1;
  padding: ${theme.spacing.small}px ${theme.spacing.medium}px;
  border: none;
  border-radius: ${theme.spacing.medium}px;
  background-color: ${theme.color.white};
  font-family: ${theme.font.body};
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.1px;
  resize: none;
  min-height: 55px;
  max-height: 120px;
  color: ${theme.color.black};

  &::placeholder {
    color: ${theme.color.grey4};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${theme.color.beigeDark};
  }

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    min-height: 130px;
    max-height: 150px;
  }
`

const SendButton = styled.button<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background-color: ${theme.color.plum};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  flex-shrink: 0;

  &:hover {
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 0.9)};
  }

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    width: 44px;
    height: 44px;
  }
`
